import { useCallback, useEffect, useState } from 'react';

/**
 * Unversendeter Text ueberlebt das Schliessen des Reiters -- F1-45.
 *
 * Der haeufigste Verlust im Ticketbetrieb ist nicht der Serverfehler,
 * sondern der geschlossene Reiter: zehn Minuten Antwort, ein versehentliches
 * Zurueck, alles fort. Was hier liegt, ist genau dieser Zwischenstand -- das
 * halb Geschriebene, das noch nicht abgeschickt ist.
 *
 * **Das ist nicht der Entwurf des Servers.** Den gibt es weiterhin ("Als
 * Entwurf sichern"): Er gehoert dem Ticket, ist fuer Kollegen sichtbar und
 * steht im Protokoll. Was hier liegt, gehoert diesem Browser und niemandem
 * sonst.
 *
 * **Und deshalb wird es wieder geloescht.** Ein Beitragstext kann
 * personenbezogene Daten enthalten -- Name, Anliegen, Rufnummer eines
 * Anrufers. Er bleibt im Browser des Bearbeiters liegen, bis er abgeschickt
 * ist, und verschwindet dann sofort. Er wird nicht ausgelesen, nicht
 * uebertragen und nicht aufbewahrt: Der Abmeldevorgang raeumt ihn mit
 * (``clearDrafts``), damit an einem geteilten Arbeitsplatz nichts vom
 * Vorgaenger stehen bleibt.
 */

const PREFIX = 'itscrm.draft.';

/**
 * Ein Textfeld, dessen Inhalt den Reiter ueberlebt.
 *
 * ``key`` muss den Ort eindeutig benennen -- Ticketkennung und Art des
 * Beitrags. Zwei Masken mit demselben Schluessel ueberschrieben einander.
 */
export function useDraft(
  key: string,
): [string, (value: string | ((current: string) => string)) => void, () => void] {
  const storageKey = PREFIX + key;

  const [value, setValue] = useState<string>(() => {
    try {
      return window.localStorage.getItem(storageKey) ?? '';
    } catch {
      // Ein Browser, der den Speicher verweigert, darf die Maske nicht
      // anhalten -- dann gibt es eben keinen geretteten Zwischenstand.
      return '';
    }
  });

  // Beim Wechsel des Tickets den Zwischenstand des neuen holen. Ohne das
  // stuende die Antwort von Ticket A im Feld von Ticket B.
  useEffect(() => {
    try {
      setValue(window.localStorage.getItem(storageKey) ?? '');
    } catch {
      setValue('');
    }
  }, [storageKey]);

  // Dieselbe Form wie ``useState``: Ein Textbaustein wird an den vorhandenen
  // Text angehaengt und kennt ihn nicht -- er braucht die Fassung mit der
  // Funktion.
  const change = useCallback(
    (next: string | ((current: string) => string)) => {
      setValue((current) => {
        const value = typeof next === 'function' ? next(current) : next;
        // Der Schreibvorgang steht in der Aktualisierungsfunktion, weil nur
        // sie den vorherigen Text kennt. React ruft sie im Entwicklungsmodus
        // zweimal auf -- unbedenklich, weil zweimal denselben Wert zu
        // schreiben dasselbe Ergebnis hat wie einmal.
        try {
          if (value === '') window.localStorage.removeItem(storageKey);
          else window.localStorage.setItem(storageKey, value);
        } catch {
          // Siehe oben.
        }
        return value;
      });
    },
    [storageKey],
  );

  const clear = useCallback(() => {
    setValue('');
    try {
      window.localStorage.removeItem(storageKey);
    } catch {
      // Siehe oben.
    }
  }, [storageKey]);

  return [value, change, clear];
}

/**
 * Raeumt alle Zwischenstaende weg.
 *
 * Beim Abmelden aufgerufen: An einem geteilten Arbeitsplatz darf der
 * Naechste nicht die halbe Antwort des Vorgaengers im Feld vorfinden.
 */
export function clearDrafts(): void {
  try {
    const keys: string[] = [];
    for (let index = 0; index < window.localStorage.length; index += 1) {
      const key = window.localStorage.key(index);
      if (key?.startsWith(PREFIX)) keys.push(key);
    }
    for (const key of keys) window.localStorage.removeItem(key);
  } catch {
    // Siehe oben.
  }
}
